import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";

const V2_FONTS = "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,300;1,9..144,400&family=Inter:wght@300;400;500;600&display=swap";

/* =========================================================================
   PreviewV2Landing — full redesign homepage preview (iter_25).
   • Lives at /preview-v2.  Live site is untouched.
   • All written copy preserved from current homepage.
   • Nav links to interior pages still work (standard anchors); if clicked
     the live-site layout opens, which is clearly flagged in the banner.
   • Visual system imported from src/styles/preview-v2.css.
   ========================================================================= */

// --- Brand assets ---------------------------------------------------------
const LOGO_CIRCLE = "https://customer-assets.emergentagent.com/job_7e80ae73-7e48-43aa-9692-c3f9d2da880e/artifacts/g7h0fdih_New%20AP%20Logo.png";
const HERO_IMG = "https://images.unsplash.com/photo-1634148739177-775032f3feb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const NARRATIVE_IMG = "https://static.prod-images.emergentagent.com/jobs/7e80ae73-7e48-43aa-9692-c3f9d2da880e/images/ac295f57b8461f32ca3051ae31cb924c1e3a48c02ad6156a256175583417ad86.jpeg";
const AUSTINE_IMG = "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/mub68y5n_Facetune_02-11-2025-15-35-15.jpeg";
const INTRO_VIDEO = "/media/austine-intro-720p.mp4";
const EXPECT_IMG = "https://images.unsplash.com/photo-1682421938316-4b186e25174c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";

// Tiny inline SVG leaf — used in place of some ✦ marks (per brief)
const Leaf = ({ size = 14, color = "currentColor", className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className={className}
    >
        <path
            d="M20 4C14 4 8 6 5 11C3 15 4 19 6 20C8 21 12 20 14 19C19 16 21 12 20 4Z"
            stroke={color}
            strokeWidth="1.2"
            fill="none"
        />
        <path d="M20 4L8 16" stroke={color} strokeWidth="1.2" />
    </svg>
);

// Decorative gold ✦
const Star = ({ className = "" }) => (
    <span
        aria-hidden
        className={className}
        style={{ fontFamily: "var(--v2-serif)", color: "var(--v2-gold)", lineHeight: 1 }}
    >
        ✦
    </span>
);

// -------------------------------------------------------------------------
//   N A V   +   T A G L I N E   S T R I P
// -------------------------------------------------------------------------
function PreviewNav() {
    const links = [
        { label: "Approach", href: "#modalities" },
        { label: "About", href: "#about" },
        { label: "Expect", href: "#expect" },
        { label: "FAQ", href: "#faq" },
        { label: "Contact", href: "#contact" },
    ];
    return (
        <>
            <div className="v2-nav">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 h-20 flex items-center justify-between">
                    <a href="#top" className="v2-brand" data-testid="v2-brand">
                        <img src={LOGO_CIRCLE} alt="Alchemy Practice" className="v2-brand-mark" />
                        <span className="v2-brand-wordmark hidden sm:inline">Alchemy Practice</span>
                    </a>
                    <nav className="hidden lg:flex items-center gap-10">
                        {links.map((l) => (
                            <a key={l.href} href={l.href} data-testid={`v2-nav-${l.label.toLowerCase()}`}>
                                {l.label}
                            </a>
                        ))}
                    </nav>
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="v2-btn-primary"
                        data-testid="v2-nav-book"
                    >
                        <span className="hidden sm:inline">Book Consultation</span>
                        <span className="sm:hidden">Book</span>
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                    </a>
                </div>
                <nav className="v2-mobile-links lg:hidden" data-testid="v2-mobile-nav">
                    {links.map((l) => (
                        <a key={l.href} href={l.href} data-testid={`v2-mobile-nav-${l.label.toLowerCase()}`}>
                            {l.label}
                        </a>
                    ))}
                </nav>
            </div>
            {/* Tagline strip — exactly as briefed */}
            <div className="v2-tagline" data-testid="v2-tagline">
                <span>Therapy</span>
                <span className="v2-tagline-mark">✦</span>
                <span>Transformation</span>
                <span className="v2-tagline-mark">✦</span>
                <span>A More Whole You</span>
            </div>
        </>
    );
}

// -------------------------------------------------------------------------
//   H E R O
// -------------------------------------------------------------------------
function PreviewHero() {
    return (
        <section id="top" className="v2-section-ivory" data-testid="v2-hero">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-20 pb-28 lg:pt-28 lg:pb-40">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-6 v2-reveal">
                        <span className="v2-eyebrow">Virtual EMDR Therapy &nbsp;·&nbsp; Wisconsin &amp; Colorado</span>
                        <h1
                            className="mt-8"
                            style={{
                                fontSize: "clamp(44px, 6vw, 88px)",
                                lineHeight: 1.02,
                                letterSpacing: "-0.015em",
                                fontWeight: 400,
                            }}
                        >
                            Where therapy becomes <em className="v2-italic">alchemy</em>.
                        </h1>
                        <p className="v2-body mt-8 max-w-xl" style={{ fontSize: 17 }}>
                            The experiences that once shaped fear and hesitation can become the foundation for clarity, confidence, and meaningful change. Therapy creates the space where transformation begins.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-6">
                            <a
                                href={BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="v2-btn-primary"
                                data-testid="v2-hero-book"
                            >
                                Schedule a Consultation
                                <ArrowUpRight size={14} strokeWidth={1.5} />
                            </a>
                            <a href="#modalities" className="v2-btn-ghost">The Approach</a>
                        </div>
                        <div className="mt-14 flex items-center gap-3">
                            <Leaf size={14} color="var(--v2-gold)" />
                            <span className="v2-eyebrow v2-eyebrow-plain">
                                Online across Wisconsin &amp; Colorado
                            </span>
                        </div>
                    </div>
                    <div className="lg:col-span-6 v2-reveal">
                        <div className="relative">
                            <img
                                src={HERO_IMG}
                                alt="A sunlit reading chair in a quiet, airy room"
                                className="w-full h-[460px] lg:h-[620px] object-cover"
                                style={{ display: "block" }}
                            />
                            {/* editorial corner mark */}
                            <div
                                className="absolute -bottom-6 -right-6 bg-[color:var(--v2-ivory)] px-5 py-4 hidden md:flex items-center gap-3"
                                style={{ border: "1px solid var(--v2-hairline)" }}
                            >
                                <Leaf size={14} color="var(--v2-gold)" />
                                <span style={{
                                    fontFamily: "var(--v2-serif)",
                                    fontStyle: "italic",
                                    color: "var(--v2-midnight)",
                                    fontSize: 14,
                                }}>
                                    A quiet place to begin
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   N A R R A T I V E   —   "You've been carrying this..."
// -------------------------------------------------------------------------
function PreviewNarrative() {
    const bullets = [
        "Anxiety, overthinking, or self-doubt",
        "Feeling emotionally overwhelmed or shut down",
        "Worry about being rejected or not good enough",
        "Trouble fully trusting other people",
        "Painful relationship patterns that repeat",
        "Shame that never quite goes away",
    ];
    return (
        <section className="v2-section-linen py-28 lg:py-40" data-testid="v2-narrative">
            <div className="mx-auto max-w-[1100px] px-6 lg:px-12">
                <div className="v2-reveal">
                    <span className="v2-eyebrow">You&rsquo;re not alone in this</span>
                    <h2
                        className="mt-6"
                        style={{
                            fontSize: "clamp(36px, 4.8vw, 68px)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.012em",
                        }}
                    >
                        You&rsquo;ve been carrying this <em className="v2-italic">for a long time.</em>
                    </h2>
                    <div className="mt-12">
                        <img
                            src={NARRATIVE_IMG}
                            alt="A quiet moment of reflection"
                            className="w-full h-[420px] lg:h-[560px] object-cover object-center"
                            style={{ display: "block" }}
                        />
                        <div className="mt-6 max-w-md">
                            <p className="v2-eyebrow" style={{ color: "var(--v2-midnight)" }}>From Austine</p>
                            <p
                                className="mt-3"
                                style={{
                                    fontFamily: "var(--v2-serif)",
                                    fontStyle: "italic",
                                    fontSize: 17,
                                    color: "var(--v2-midnight-soft)",
                                    lineHeight: 1.7,
                                }}
                            >
                                If any of what follows feels familiar — even a small piece of it — you are not alone, and you are not too late.
                            </p>
                        </div>
                    </div>

                    <div className="mt-14 v2-hairline" />

                    <p
                        className="mt-12 max-w-2xl"
                        style={{
                            fontFamily: "var(--v2-serif)",
                            fontSize: 20,
                            lineHeight: 1.75,
                            color: "var(--v2-midnight)",
                        }}
                    >
                        You&rsquo;ve spent years trying to move forward, telling yourself you should be over it by now. Maybe you&rsquo;ve become good at staying busy, pushing your feelings aside, or pretending everything is fine. From the outside, it may even look like you have it together. But underneath, you still carry the weight of what happened.
                    </p>
                    <p className="v2-body mt-6 max-w-2xl">
                        The emotional wounds from your past — the rejection, the criticism, the quiet rooms where you felt unseen — still affect you in ways that are painful and exhausting. You second-guess yourself. You worry people will judge you, leave you, or discover that you are somehow not enough.
                    </p>
                    <p className="v2-body mt-6 max-w-2xl">
                        Sometimes you can keep those feelings buried. Then something small happens — a certain tone of voice, a difficult conversation, a memory in a quiet moment — and suddenly you are overwhelmed.
                    </p>

                    <blockquote
                        className="mt-12 max-w-2xl pl-6"
                        style={{
                            borderLeft: "2px solid var(--v2-gold)",
                            fontFamily: "var(--v2-serif)",
                            fontStyle: "italic",
                            fontSize: 22,
                            lineHeight: 1.5,
                            color: "var(--v2-gold)",
                        }}
                    >
                        If this sounds familiar, you are not broken. You are carrying what you had to in order to survive.
                    </blockquote>

                    <div className="mt-14">
                        <p className="v2-eyebrow" style={{ color: "var(--v2-midnight)" }}>You might notice that you…</p>
                        <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-4">
                            {bullets.map((b) => (
                                <li key={b} className="flex items-start gap-3 v2-body" style={{ fontSize: 15 }}>
                                    <span style={{ marginTop: 10, height: 1, width: 16, background: "var(--v2-gold)", flexShrink: 0 }} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Lazy intro video with bottom-left pill play button (mirrors live site)
function V2IntroVideo() {
    const videoRef = React.useRef(null);
    const wrapRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [inView, setInView] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);

    React.useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setInView(true);
            return undefined;
        }
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setInView(true);
                    io.disconnect();
                }
            },
            { rootMargin: "200px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const handlePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = false;
        setPlaying(true);
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
    };

    return (
        <div ref={wrapRef} className="relative w-full h-[380px] lg:h-[520px] overflow-hidden" data-testid="v2-intro-video-wrap">
            <video
                ref={videoRef}
                poster={AUSTINE_IMG}
                preload="none"
                playsInline
                controls={playing}
                className="w-full h-full object-cover object-center block"
                data-testid="v2-intro-video"
            >
                {inView ? <source src={INTRO_VIDEO} type="video/mp4" /> : null}
            </video>
            {!playing && (
                <button
                    type="button"
                    onClick={handlePlay}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onFocus={() => setHovered(true)}
                    onBlur={() => setHovered(false)}
                    aria-label="Play a short welcome from Austine"
                    data-testid="v2-intro-video-play"
                    className="absolute inset-0 flex items-end justify-start focus:outline-none cursor-pointer"
                >
                    <span
                        aria-hidden
                        className="v2-video-pill m-4 lg:m-5"
                        style={{ background: hovered ? "rgba(26,46,74,0.88)" : "rgba(26,46,74,0.64)" }}
                    >
                        <svg width="11" height="13" viewBox="0 0 12 14" fill="none" aria-hidden>
                            <path d="M1 1L11 7L1 13V1Z" fill="#CFA969" />
                        </svg>
                        Play Video
                    </span>
                </button>
            )}
        </div>
    );
}

// -------------------------------------------------------------------------
//   M E E T   A U S T I N E
// -------------------------------------------------------------------------
function PreviewAbout() {
    const creds = ["LPC — Wisconsin", "LPC — Colorado", "Telehealth Only"];
    return (
        <section id="about" className="v2-section-ivory py-28 lg:py-40" data-testid="v2-about">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-7 v2-reveal">
                        <span className="v2-eyebrow">Meet Austine</span>
                        <h2
                            className="mt-6"
                            style={{
                                fontSize: "clamp(36px, 4.6vw, 64px)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.012em",
                            }}
                        >
                            Virtual EMDR therapy in{" "}
                            <em className="v2-italic">Wisconsin &amp; Colorado.</em>
                        </h2>
                        <div className="mt-10 v2-hairline" style={{ width: 64 }} />
                        <p className="v2-body mt-10 max-w-xl">
                            I&rsquo;m Austine — a licensed professional counselor offering virtual therapy to thoughtful adults in Wisconsin and Colorado. I help people who are struggling with anxiety, trauma, self-doubt, and the lasting weight of painful experiences.
                        </p>
                        <p className="v2-body mt-5 max-w-xl">
                            I believe therapy should feel compassionate, safe, and quietly sophisticated — a place where you do not have to explain everything perfectly or have all the right words. We start where you are.
                        </p>
                        <p className="v2-body mt-5 max-w-xl">
                            In our work together, you will not have to keep carrying this alone. Therapy can become a place where you understand yourself with more kindness, where you feel safer in your body, and where you reconnect with the parts of yourself that have been buried under years of fear and survival.
                        </p>

                        <blockquote
                            className="mt-10 pl-6 max-w-xl"
                            style={{
                                borderLeft: "2px solid var(--v2-gold)",
                                fontFamily: "var(--v2-serif)",
                                fontStyle: "italic",
                                fontSize: 22,
                                lineHeight: 1.5,
                                color: "var(--v2-gold)",
                            }}
                        >
                            You are not defined by what happened to you.
                        </blockquote>

                        <div className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-3">
                            {creds.map((c) => (
                                <span key={c} className="flex items-center gap-2 v2-eyebrow v2-eyebrow-plain" style={{ color: "var(--v2-midnight)" }}>
                                    <Leaf size={11} color="var(--v2-gold)" />
                                    {c}
                                </span>
                            ))}
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-x-10 gap-y-2">
                            <span style={{
                                fontFamily: "var(--v2-serif)",
                                fontStyle: "italic",
                                color: "var(--v2-gold)",
                                fontSize: 15,
                            }}>EMDRIA Training &amp; Certification in Process</span>
                            <span style={{ color: "var(--v2-gold)" }}>✦</span>
                            <span style={{
                                fontFamily: "var(--v2-serif)",
                                fontStyle: "italic",
                                color: "var(--v2-gold)",
                                fontSize: 15,
                            }}>Brainspotting Practitioner</span>
                        </div>

                        <a
                            href={BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="v2-btn-primary mt-12"
                            data-testid="v2-about-book"
                        >
                            Schedule a Consultation
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </a>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2 v2-reveal">
                        <div
                            className="relative mx-auto lg:ml-auto"
                            style={{ maxWidth: 420, width: "100%" }}
                        >
                            <V2IntroVideo />
                            <div
                                className="absolute -top-3 -left-3 px-4 py-2 flex items-center gap-2 pointer-events-none"
                                style={{
                                    background: "var(--v2-ivory)",
                                    border: "1px solid var(--v2-gold)",
                                }}
                            >
                                <Leaf size={11} color="var(--v2-gold)" />
                                <span style={{
                                    fontFamily: "var(--v2-serif)",
                                    fontStyle: "italic",
                                    color: "var(--v2-midnight)",
                                    fontSize: 13,
                                }}>Austine, LPC</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   S P E C I A L T I E S  (Modalities)
// -------------------------------------------------------------------------
function PreviewSpecialties() {
    const items = [
        {
            key: "emdr",
            n: "I.",
            title: "EMDR",
            href: "/emdr",
            subtitle: "Eye Movement Desensitization & Reprocessing",
            body: "For when fear and earlier experiences still quietly shape your decisions. EMDR helps the brain process what remained unfinished, so memories no longer carry the same emotional weight.",
            extra: { href: "/emdr-intensive", label: "Also offering: EMDR Intensives →" },
        },
        {
            key: "brainspotting",
            n: "II.",
            title: "Brainspotting",
            href: "/brainspotting",
            subtitle: "A precise, body-aware approach",
            body: "Brainspotting reaches the places that talk therapy cannot easily touch — accessing the body's own capacity to release and reorganize what trauma left behind.",
        },
        {
            key: "act",
            n: "III.",
            title: "ACT",
            href: "/act",
            subtitle: "Acceptance & Commitment Therapy",
            body: "Fear often appears the moment we move toward the life we want. ACT helps you build psychological flexibility — and step forward with intention, even when fear is in the room.",
        },
        {
            key: "dbt",
            n: "IV.",
            title: "DBT-informed",
            href: "/dbt",
            subtitle: "Dialectical Behavior Therapy skills",
            body: "Practical tools for navigating intense emotions, tolerating distress, and staying grounded in the difficult moments — so deeper work becomes possible.",
        },
    ];
    return (
        <section id="modalities" className="v2-section-linen py-28 lg:py-40" data-testid="v2-specialties">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                <div className="max-w-2xl v2-reveal">
                    <span className="v2-eyebrow">Areas of Expertise</span>
                    <h2
                        className="mt-6"
                        style={{
                            fontSize: "clamp(36px, 4.6vw, 64px)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.012em",
                        }}
                    >
                        Four <em className="v2-italic">careful</em> ways of doing this work.
                    </h2>
                </div>

                <div className="mt-16 grid md:grid-cols-2 gap-6" data-testid="v2-specialties-grid">
                    {items.map((m) => (
                        <div
                            key={m.key}
                            className={`v2-spec-card v2-spec-${m.key}`}
                            data-testid={`v2-spec-${m.key}`}
                        >
                            <a href={m.href} className="flex flex-col h-full">
                                <div className="flex items-start justify-between mb-6">
                                    <span className="v2-spec-numeral">{m.n}</span>
                                    <Leaf size={16} color="var(--accent)" className="v2-spec-leaf" />
                                </div>
                                <h3
                                    style={{
                                        fontSize: 36,
                                        lineHeight: 1.1,
                                        marginBottom: 6,
                                    }}
                                >
                                    {m.title}
                                </h3>
                                <p
                                    style={{
                                        fontFamily: "var(--v2-sans)",
                                        fontSize: 11,
                                        letterSpacing: "0.26em",
                                        textTransform: "uppercase",
                                        color: "var(--v2-gold)",
                                        marginBottom: 24,
                                        fontWeight: 500,
                                    }}
                                >
                                    {m.subtitle}
                                </p>
                                <div style={{ height: 1, width: 40, background: "var(--accent)", marginBottom: 24 }} />
                                <p className="v2-body" style={{ fontSize: 15, flex: 1 }}>
                                    {m.body}
                                </p>
                                <div
                                    style={{
                                        marginTop: 24,
                                        fontSize: 11,
                                        letterSpacing: "0.26em",
                                        textTransform: "uppercase",
                                        color: "var(--v2-gold)",
                                        fontWeight: 500,
                                    }}
                                >
                                    Read more →
                                </div>
                            </a>
                            {m.extra && (
                                <>
                                    <div className="mt-6 v2-hairline" />
                                    <a
                                        href={m.extra.href}
                                        style={{
                                            marginTop: 14,
                                            fontFamily: "var(--v2-serif)",
                                            fontStyle: "italic",
                                            color: "var(--v2-gold)",
                                            fontSize: 15,
                                        }}
                                    >
                                        {m.extra.label}
                                    </a>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   P H I L O S O P H Y   —   one dark feature section per page
// -------------------------------------------------------------------------
function PreviewPhilosophy() {
    return (
        <section
            className="v2-section-midnight py-28 lg:py-40"
            data-testid="v2-philosophy"
        >
            <div className="mx-auto max-w-[1000px] px-6 lg:px-12 text-center v2-reveal">
                <div className="flex justify-center">
                    <Leaf size={22} color="var(--v2-gold)" />
                </div>
                <span className="v2-eyebrow mt-8" style={{ color: "var(--v2-gold-light)" }}>
                    A Philosophy of Care
                </span>
                <h2
                    className="mt-8"
                    style={{
                        fontSize: "clamp(32px, 4.4vw, 56px)",
                        lineHeight: 1.1,
                        color: "var(--v2-ivory)",
                        fontFamily: "var(--v2-serif)",
                        fontWeight: 400,
                    }}
                >
                    Therapy that is{" "}
                    <em style={{
                        fontFamily: "var(--v2-serif)",
                        fontStyle: "italic",
                        color: "var(--v2-gold)",
                    }}>illuminous, wise, and warm</em>{" "}
                    — grounded in clinical rigor, held in quiet elegance.
                </h2>
                <p
                    className="mt-10 mx-auto max-w-2xl"
                    style={{
                        fontFamily: "var(--v2-sans)",
                        fontSize: 16,
                        lineHeight: 1.85,
                        color: "var(--v2-ivory)",
                        opacity: 0.88,
                    }}
                >
                    This is a practice built on timeless, restorative work. The goal is not to make the past smaller — it is to make you more whole. Bright, considered, and entirely yours.
                </p>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   W H A T   T O   E X P E C T
// -------------------------------------------------------------------------
function PreviewWhatToExpect() {
    const steps = [
        {
            n: "01",
            accent: "var(--v2-sage)",
            title: "A quiet first conversation",
            body: "Our consultation is short, warm, and pressure-free. We talk about what's bringing you here — and decide together if it feels like a fit.",
        },
        {
            n: "02",
            accent: "var(--v2-mauve)",
            title: "We move at your pace",
            body: "Nothing about this work is rushed. We begin where you are — gently — and let each session build on the last with care and intention.",
        },
        {
            n: "03",
            accent: "var(--v2-cornflower)",
            title: "A practice, not a performance",
            body: "You don't need the right words. You don't need to arrive composed. Therapy here is private, considered, and entirely yours.",
        },
    ];
    return (
        <section id="expect" className="v2-section-ivory py-28 lg:py-40" data-testid="v2-expect">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5 v2-reveal">
                        <img
                            src={EXPECT_IMG}
                            alt="A quiet, candlelit afternoon"
                            className="w-full h-[460px] lg:h-[620px] object-cover"
                        />
                    </div>
                    <div className="lg:col-span-7 lg:pl-6 v2-reveal">
                        <span className="v2-eyebrow">What to expect</span>
                        <h2
                            className="mt-6"
                            style={{
                                fontSize: "clamp(36px, 4.6vw, 64px)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.012em",
                            }}
                        >
                            The first step is the <em className="v2-italic">softest</em> one.
                        </h2>
                        <div className="mt-8 v2-hairline" style={{ width: 64 }} />
                        <p className="v2-body mt-8 max-w-xl">
                            Beginning therapy doesn&rsquo;t have to feel like a leap. It can feel like sitting down with a warm cup, breathing once, and starting a quiet conversation.
                        </p>

                        <ol className="mt-12 space-y-10" data-testid="v2-expect-steps">
                            {steps.map((s) => (
                                <li key={s.n} className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 items-start">
                                    <span
                                        style={{
                                            fontFamily: "var(--v2-serif)",
                                            fontStyle: "italic",
                                            fontSize: 28,
                                            color: s.accent,
                                            lineHeight: 1,
                                            marginTop: 6,
                                        }}
                                    >
                                        {s.n}
                                    </span>
                                    <div>
                                        <h3 style={{ fontSize: 24, lineHeight: 1.2 }}>{s.title}</h3>
                                        <p className="v2-body mt-3 max-w-xl" style={{ fontSize: 15 }}>{s.body}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <a
                            href={BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="v2-btn-primary mt-14"
                            data-testid="v2-expect-book"
                        >
                            Begin When You&rsquo;re Ready
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   F A Q   (preserved copy from current site)
// -------------------------------------------------------------------------
function PreviewFAQ() {
    const items = [
        {
            q: "How do I know if we are a good fit?",
            a: "The best way is a short consultation. We will talk about what is bringing you to therapy and what you would like to feel differently. There is no pressure to continue — fit matters, and I would rather be honest about it than wrong about it.",
        },
        {
            q: "What is EMDR, in plain language?",
            a: "EMDR helps the brain process memories that feel stuck — the ones that still cause pain or shape current reactions. It uses gentle, structured attention and bilateral stimulation to help the nervous system complete what it could not finish at the time.",
        },
        {
            q: "Do you take insurance?",
            a: "I am an out-of-network provider. I can provide a superbill for you to submit for possible reimbursement. A Good Faith Estimate is available on request, in accordance with the No Surprises Act.",
        },
        {
            q: "Where can I see what you charge?",
            a: "Please reach out and I will share current rates, along with a Good Faith Estimate before we begin. I believe fees should be clear and never a surprise.",
        },
        {
            q: "What is virtual therapy actually like?",
            a: "A private video session through a secure platform. Many clients find it easier to settle in from home — a familiar chair, a warm cup, a candle lit nearby. The therapeutic container is still intentional, held, and real.",
        },
    ];
    const [open, setOpen] = React.useState(0);
    return (
        <section id="faq" className="v2-section-linen py-28 lg:py-40" data-testid="v2-faq">
            <div className="mx-auto max-w-[1000px] px-6 lg:px-12">
                <div className="text-center v2-reveal">
                    <span className="v2-eyebrow">Thoughtful Answers</span>
                    <h2
                        className="mt-6"
                        style={{
                            fontSize: "clamp(36px, 4.6vw, 60px)",
                            lineHeight: 1.05,
                        }}
                    >
                        A few things people <em className="v2-italic">often wonder</em>.
                    </h2>
                </div>
                <div className="mt-16">
                    {items.map((it, i) => (
                        <div
                            key={it.q}
                            style={{ borderTop: i === 0 ? "1px solid var(--v2-hairline)" : "none", borderBottom: "1px solid var(--v2-hairline)" }}
                            data-testid={`v2-faq-item-${i}`}
                        >
                            <button
                                type="button"
                                onClick={() => setOpen(open === i ? -1 : i)}
                                className="w-full flex items-center justify-between py-6 text-left"
                                style={{ background: "transparent" }}
                            >
                                <span
                                    style={{
                                        fontFamily: "var(--v2-serif)",
                                        fontSize: 22,
                                        color: "var(--v2-midnight)",
                                        fontWeight: 400,
                                    }}
                                >
                                    {it.q}
                                </span>
                                <span style={{ color: "var(--v2-gold)", fontSize: 22, lineHeight: 1 }}>
                                    {open === i ? "–" : "+"}
                                </span>
                            </button>
                            {open === i && (
                                <p className="v2-body pb-8 pr-10" style={{ fontSize: 15, maxWidth: 760 }}>
                                    {it.a}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
                <p className="v2-body text-center mt-12 max-w-xl mx-auto" style={{ fontSize: 13, opacity: 0.75 }}>
                    In keeping with ethical guidelines, this practice does not publish client testimonials.
                </p>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   C O N T A C T   +   N E W S L E T T E R
// -------------------------------------------------------------------------
function PreviewContact() {
    return (
        <section id="contact" className="v2-section-ivory py-28 lg:py-40" data-testid="v2-contact">
            <div className="mx-auto max-w-[1100px] px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
                    <div className="v2-reveal">
                        <span className="v2-eyebrow">Begin a Conversation</span>
                        <h2
                            className="mt-6"
                            style={{
                                fontSize: "clamp(32px, 4vw, 52px)",
                                lineHeight: 1.1,
                            }}
                        >
                            Reach out when it feels <em className="v2-italic">right</em>.
                        </h2>
                        <p className="v2-body mt-8 max-w-md">
                            Share a few words about what&rsquo;s bringing you here. I read every message personally and will respond within two business days.
                        </p>
                        <div className="mt-10 v2-hairline" style={{ width: 48 }} />
                        <p className="v2-body mt-10 max-w-md" style={{ fontSize: 13 }}>
                            In a crisis? Call or text <strong style={{ color: "var(--v2-midnight)" }}>988</strong> for the Suicide &amp; Crisis Lifeline. If you are in immediate danger, call 911.
                        </p>
                    </div>
                    <form
                        className="v2-reveal space-y-6"
                        onSubmit={(e) => e.preventDefault()}
                        data-testid="v2-contact-form"
                    >
                        {["Full Name", "Email Address", "Phone (Optional)"].map((label) => (
                            <div key={label}>
                                <label
                                    style={{
                                        fontFamily: "var(--v2-sans)",
                                        fontSize: 11,
                                        letterSpacing: "0.26em",
                                        textTransform: "uppercase",
                                        color: "var(--v2-midnight-soft)",
                                        fontWeight: 500,
                                    }}
                                >
                                    {label}
                                </label>
                                <input
                                    type={label.startsWith("Email") ? "email" : "text"}
                                    style={{
                                        width: "100%",
                                        marginTop: 8,
                                        padding: "12px 0",
                                        background: "transparent",
                                        border: "none",
                                        borderBottom: "1px solid var(--v2-hairline)",
                                        fontFamily: "var(--v2-serif)",
                                        fontSize: 18,
                                        color: "var(--v2-midnight)",
                                        outline: "none",
                                    }}
                                    onFocus={(e) => (e.target.style.borderBottomColor = "var(--v2-gold)")}
                                    onBlur={(e) => (e.target.style.borderBottomColor = "var(--v2-hairline)")}
                                />
                            </div>
                        ))}
                        <div>
                            <label
                                style={{
                                    fontFamily: "var(--v2-sans)",
                                    fontSize: 11,
                                    letterSpacing: "0.26em",
                                    textTransform: "uppercase",
                                    color: "var(--v2-midnight-soft)",
                                    fontWeight: 500,
                                }}
                            >
                                What&rsquo;s bringing you here
                            </label>
                            <textarea
                                rows={4}
                                style={{
                                    width: "100%",
                                    marginTop: 8,
                                    padding: "12px 0",
                                    background: "transparent",
                                    border: "none",
                                    borderBottom: "1px solid var(--v2-hairline)",
                                    fontFamily: "var(--v2-serif)",
                                    fontSize: 18,
                                    color: "var(--v2-midnight)",
                                    outline: "none",
                                    resize: "vertical",
                                }}
                                onFocus={(e) => (e.target.style.borderBottomColor = "var(--v2-gold)")}
                                onBlur={(e) => (e.target.style.borderBottomColor = "var(--v2-hairline)")}
                            />
                        </div>
                        <button type="submit" className="v2-btn-primary">
                            Send Message
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </button>
                    </form>
                </div>

                {/* Newsletter */}
                <div
                    className="mt-28 lg:mt-36 p-10 lg:p-14 text-center v2-reveal"
                    style={{
                        background: "var(--v2-linen)",
                        border: "1px solid var(--v2-hairline)",
                    }}
                >
                    <Leaf size={22} color="var(--v2-gold)" className="mx-auto" />
                    <span className="v2-eyebrow mt-6" style={{ color: "var(--v2-midnight)" }}>Alchemy Insights</span>
                    <h3
                        className="mt-5"
                        style={{
                            fontSize: "clamp(24px, 3vw, 36px)",
                            lineHeight: 1.15,
                        }}
                    >
                        Quiet letters, sent when they&rsquo;re <em className="v2-italic">worth sending</em>.
                    </h3>
                    <p className="v2-body mt-5 max-w-xl mx-auto" style={{ fontSize: 15 }}>
                        Occasional reflections on EMDR, nervous system care, and the kind of therapy that makes you feel more whole. No noise.
                    </p>
                    <form
                        className="mt-8 flex flex-wrap gap-4 justify-center items-center"
                        onSubmit={(e) => e.preventDefault()}
                        data-testid="v2-newsletter-form"
                    >
                        <input
                            type="email"
                            placeholder="your@email.com"
                            style={{
                                background: "var(--v2-ivory)",
                                border: "1px solid var(--v2-hairline)",
                                padding: "14px 20px",
                                minWidth: 280,
                                fontFamily: "var(--v2-serif)",
                                fontStyle: "italic",
                                color: "var(--v2-midnight)",
                                outline: "none",
                            }}
                        />
                        <button type="submit" className="v2-btn-primary">
                            Subscribe
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
//   F O O T E R
// -------------------------------------------------------------------------
function PreviewFooter() {
    return (
        <footer className="v2-footer py-20" data-testid="v2-footer">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4">
                            <img
                                src={LOGO_CIRCLE}
                                alt="Alchemy Practice"
                                style={{ width: 72, height: 72, objectFit: "contain" }}
                            />
                            <span
                                style={{
                                    fontFamily: "var(--v2-serif)",
                                    fontSize: 24,
                                    letterSpacing: "0.1em",
                                    textTransform: "uppercase",
                                    color: "var(--v2-ivory)",
                                }}
                            >
                                Alchemy Practice
                            </span>
                        </div>
                        <p
                            className="mt-6 max-w-sm"
                            style={{
                                fontFamily: "var(--v2-serif)",
                                fontStyle: "italic",
                                fontSize: 18,
                                color: "var(--v2-gold-light)",
                                lineHeight: 1.55,
                            }}
                        >
                            Therapy ✦ Transformation ✦ A More Whole You
                        </p>
                        <p className="v2-body mt-6 max-w-sm" style={{ color: "var(--v2-ivory)", opacity: 0.75, fontSize: 14 }}>
                            Virtual EMDR therapy for thoughtful adults in Wisconsin &amp; Colorado.
                        </p>
                    </div>
                    <div className="lg:col-span-3">
                        <p className="v2-eyebrow" style={{ color: "var(--v2-gold-light)" }}>Explore</p>
                        <ul className="mt-5 space-y-3" style={{ fontSize: 14 }}>
                            <li><a href="#modalities">Approach</a></li>
                            <li><a href="#about">About</a></li>
                            <li><a href="#expect">What to Expect</a></li>
                            <li><a href="#faq">FAQ</a></li>
                            <li><a href="#contact">Contact</a></li>
                        </ul>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="v2-eyebrow" style={{ color: "var(--v2-gold-light)" }}>Practice Notes</p>
                        <p className="v2-body mt-5" style={{ color: "var(--v2-ivory)", opacity: 0.75, fontSize: 13 }}>
                            A Good Faith Estimate is available on request, in accordance with the No Surprises Act. In crisis? Call or text <strong style={{ color: "var(--v2-ivory)" }}>988</strong>.
                        </p>
                        <p className="v2-body mt-5" style={{ color: "var(--v2-ivory)", opacity: 0.6, fontSize: 12 }}>
                            In keeping with ethical guidelines, this practice does not publish client testimonials.
                        </p>
                    </div>
                </div>
                <div className="mt-16 pt-8 flex flex-wrap justify-between items-center gap-4" style={{ borderTop: "1px solid rgba(207,169,105,0.3)" }}>
                    <p style={{ fontSize: 11, letterSpacing: "0.26em", textTransform: "uppercase", color: "var(--v2-gold-light)" }}>
                        © 2026 Alchemy Practice
                    </p>
                    <p style={{ fontSize: 11, letterSpacing: "0.26em", textTransform: "uppercase", color: "var(--v2-gold-light)" }}>
                        Austine, LPC — WI &amp; CO
                    </p>
                </div>
            </div>
        </footer>
    );
}

// -------------------------------------------------------------------------
//   P A G E
// -------------------------------------------------------------------------
export default function PreviewV2Landing() {
    return (
        <div data-theme="preview-v2" data-testid="v2-root" className="v2-banner-pad">
            <Seo
                title="Alchemy Practice — Full Redesign Preview"
                description="Full brand redesign preview — not live"
            />
            <Helmet>
                <link rel="stylesheet" href={V2_FONTS} />
            </Helmet>
            <div className="v2-banner" data-testid="v2-banner">
                <span className="hidden sm:inline">Full Redesign Preview — </span><strong>Ivory · Midnight · Gold</strong> — not live
            </div>
            <PreviewNav />
            <PreviewHero />
            <PreviewNarrative />
            <PreviewAbout />
            <PreviewSpecialties />
            <PreviewPhilosophy />
            <PreviewWhatToExpect />
            <PreviewFAQ />
            <PreviewContact />
            <PreviewFooter />
        </div>
    );
}
